/**
 * Dev Error Store
 *
 * Ring buffer of recent errors for the developer error panel.
 * Stores the last 50 errors with structured context for debugging.
 *
 * @since 0.1.0
 * @example
 * ```typescript
 * import { devErrorStore } from "@ascleo/client/stores/dev-error.store";
 *
 * devErrorStore.push({ error, context: { layer: "collection", source: "patient" } });
 * const errors = devErrorStore.getErrors();
 * const unsubscribe = devErrorStore.subscribe((errors) => setErrors(errors));
 * ```
 */

import { systemClock } from "@ascleo/types/date-time-validity";

/**
 * Error entry stored in the dev error store.
 *
 * @since 0.1.0
 */
export interface DevErrorEntry {
  /** Unique identifier for this error entry */
  readonly id: string;
  /** Timestamp when the error was recorded */
  readonly timestamp: number;
  /** The error object */
  readonly error: unknown;
  /** User-facing message (extracted from MutationErrorUnion if available) */
  readonly userMessage: string;
  /** Developer-facing message with technical details */
  readonly developerMessage: string;
  /** Layer where the error occurred */
  readonly layer: "form" | "mutation" | "collection" | "rpc" | "unknown";
  /** Source identifier (e.g., collection name, form name) */
  readonly source: string;
  /** Stack trace if available */
  readonly stack?: string;
}

type Subscriber = (errors: ReadonlyArray<DevErrorEntry>) => void;

const MAX_ERRORS = 50;

// Immutable snapshot — same reference returned from getSnapshot() until data changes.
// This is required by React's useSyncExternalStore to avoid infinite re-render loops.
let snapshot: ReadonlyArray<DevErrorEntry> = [];
let nextId = 1;
const subscribers = new Set<Subscriber>();

function notifySubscribers(): void {
  for (const subscriber of subscribers) {
    subscriber(snapshot);
  }
}

/**
 * Push a new error entry to the store.
 *
 * @since 0.1.0
 */
function push(entry: Omit<DevErrorEntry, "id" | "timestamp">): DevErrorEntry {
  const fullEntry: DevErrorEntry = {
    ...entry,
    id: `err-${nextId++}`,
    timestamp: systemClock.now().getTime(),
  };

  snapshot = Object.freeze([fullEntry, ...snapshot].slice(0, MAX_ERRORS));
  notifySubscribers();
  return fullEntry;
}

/**
 * Get all stored errors (most recent first).
 *
 * Returns a referentially stable array — same reference until data changes.
 * Safe for use with `useSyncExternalStore`.
 *
 * @since 0.1.0
 */
function getErrors(): ReadonlyArray<DevErrorEntry> {
  return snapshot;
}

/**
 * Get the count of stored errors.
 *
 * @since 0.1.0
 */
function getCount(): number {
  return snapshot.length;
}

/**
 * Subscribe to error changes. Returns an unsubscribe function.
 *
 * @since 0.1.0
 */
function subscribe(callback: Subscriber): () => void {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

/**
 * Clear all stored errors.
 *
 * @since 0.1.0
 */
function clear(): void {
  snapshot = [];
  notifySubscribers();
}

/**
 * Singleton dev error store for collecting errors across the application.
 *
 * @since 0.1.0
 */
export const devErrorStore = {
  push,
  getErrors,
  getCount,
  subscribe,
  clear,
} as const;
